import React, { useCallback, useEffect, useState } from "react";

import { Link as ReactLink, useHistory } from "react-router-dom";

import { Flex, Link, Text } from "@chakra-ui/react";

import FiltersAuditoria from "../../components/FiltersAuditoria";
import TableAuditoria from "../../components/TableAuditoria";
import AcaoAuditoriaBadge from "../../components/AcaoAuditoriaBadge";
import MenuOpcoesAuditoria, {
  ItemVisualizar,
} from "../../components/MenuOpcoesAuditoria";
import AuditoriaService from "../../../main/services/AuditoriaService";
import { useAuthProvider } from "../../../Context/AuthProvider";

const STORAGE_KEY = "auditoria-filter";

const lerStorage = () =>
  JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");

const persistir = (overrides) => {
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...lerStorage(), ...overrides }),
  );
};

const Auditoria = () => {
  document.title = "MCA - Auditoria";

  const history = useHistory();
  const { roleKeycloack } = useAuthProvider();

  const [visao, setVisao] = useState(() => lerStorage().visao || "SERVICO");
  const [filters, setFilters] = useState(() => lerStorage().filters || {});
  const [chips, setChips] = useState(() => lerStorage().chips || []);
  const [pageIndex, setPageIndex] = useState(() => lerStorage().page || 0);
  const [pageSize, setPageSize] = useState(() => lerStorage().pageSize || 10);
  const [result, setResult] = useState({
    content: [],
    totalElements: 0,
    totalPages: 1,
  });
  const [sortField, setSortField] = useState(
    () => lerStorage().sortField || "nome",
  );
  const [sortType, setSortType] = useState(
    () => lerStorage().sortType || "ASC",
  );
  const [loading, setLoading] = useState(false);
  const [pesquisou, setPesquisou] = useState(false);

  const buscar = useCallback(
    (visaoAtual, filtrosAtuais, pagina, tamanhoPagina, campo, tipo) => {
      setLoading(true);
      AuditoriaService.pesquisar(
        visaoAtual,
        filtrosAtuais,
        pagina,
        tamanhoPagina,
        campo,
        tipo,
      )
        .then((res) => {
          setResult(res.data);
          setPesquisou(true);
        })
        .finally(() => setLoading(false));
    },
    [],
  );

  useEffect(() => {
    const inicial = lerStorage();
    if (inicial.pesquisou) {
      buscar(
        inicial.visao || "SERVICO",
        inicial.filters || {},
        inicial.page || 0,
        inicial.pageSize || 10,
        inicial.sortField || "nome",
        inicial.sortType || "ASC",
      );
    }
  }, [buscar]);

  const handleSearch = (values, filterChips, visaoEscolhida) => {
    setVisao(visaoEscolhida);
    setFilters(values);
    setChips(filterChips || []);
    setPageIndex(0);
    persistir({
      visao: visaoEscolhida,
      filters: values,
      chips: filterChips || [],
      page: 0,
      pesquisou: true,
    });
    buscar(visaoEscolhida, values, 0, pageSize, sortField, sortType);
  };

  const handleVisaoChange = (novaVisao) => {
    setVisao(novaVisao);
    setFilters({});
    setChips([]);
    setPageIndex(0);
    setSortField("nome");
    setSortType("ASC");
    setResult({ content: [], totalElements: 0, totalPages: 1 });
    setPesquisou(false);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const handleClear = () => {
    setVisao("SERVICO");
    setFilters({});
    setChips([]);
    setPageIndex(0);
    sessionStorage.removeItem(STORAGE_KEY);
    buscar("SERVICO", {}, 0, pageSize, sortField, sortType);
  };

  const handlePageChange = (novaPagina) => {
    setPageIndex(novaPagina);
    persistir({ page: novaPagina });
    buscar(visao, filters, novaPagina, pageSize, sortField, sortType);
  };

  const handlePageSizeChange = (novoPageSize) => {
    setPageSize(novoPageSize);
    setPageIndex(0);
    persistir({ pageSize: novoPageSize, page: 0 });
    buscar(visao, filters, 0, novoPageSize, sortField, sortType);
  };

  const handleSort = (campo) => {
    const novoTipo = sortField === campo && sortType === "ASC" ? "DESC" : "ASC";
    setSortField(campo);
    setSortType(novoTipo);
    setPageIndex(0);
    persistir({ sortField: campo, sortType: novoTipo, page: 0 });
    buscar(visao, filters, 0, pageSize, campo, novoTipo);
  };

  const colunaNome = (rotaVisao) => ({
    Header:
      rotaVisao === "SERVICO"
        ? "Serviço de Acolhimento"
        : "Criança/Adolescente",
    accessor: "nome",
    Cell: (props) => (
      <Flex flexDir="column">
        <Link
          as={ReactLink}
          to={{
            pathname: `/auditoria/${rotaVisao === "SERVICO" ? "servico" : "crianca"}/${props.row.original.registroDk}`,
            state: { chips, filters },
          }}
        >
          {props.value}
        </Link>
        {rotaVisao !== "SERVICO" && (
          <Text fontSize="12px" color="#6B6C71">
            {[
              props.row.original.idade != null &&
                `${props.row.original.idade} anos`,
              props.row.original.dataNascimento,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        )}
      </Flex>
    ),
  });

  const colunaOpcoes = (rotaVisao) => ({
    Header: "Opções",
    accessor: "registroDk",
    disableSort: true,
    Cell: (props) => (
      <MenuOpcoesAuditoria
        visao={rotaVisao}
        id={props.value}
        filtrosOrigem={filters}
        visualizar={
          <ItemVisualizar
            onClick={() =>
              history.push(
                `/auditoria/${rotaVisao === "SERVICO" ? "servico" : "crianca"}/${props.value}`,
                { chips, filters },
              )
            }
          />
        }
      />
    ),
  });

  const columnsServico = [
    colunaNome("SERVICO"),
    { Header: "Data Último Evento", accessor: "dataHoraUltimoEvento" },
    {
      Header: "Tipo Último Evento",
      accessor: "tipoUltimoEvento",
      Cell: (props) => <AcaoAuditoriaBadge acao={props.value} />,
    },
    colunaOpcoes("SERVICO"),
  ];

  const columnsCrianca = [
    colunaNome("CRIANCA"),
    {
      Header: "Filiação",
      accessor: "filiacao",
      Cell: (props) =>
        (props.value || "")
          .split(", ")
          .filter(Boolean)
          .map((linha, i) => (
            <Text key={i} whiteSpace="nowrap">
              {linha}
            </Text>
          )),
    },
    { Header: "Serviço Vinculado", accessor: "nomeServicoVinculado" },
    { Header: "Data Último Evento", accessor: "dataHoraUltimoEvento" },
    {
      Header: "Tipo Último Evento",
      accessor: "tipoUltimoEvento",
      Cell: (props) => <AcaoAuditoriaBadge acao={props.value} />,
    },
    colunaOpcoes("CRIANCA"),
  ];

  return (
    <Flex
      flex={1}
      flexDir="column"
      px={["5px", "32px"]}
      pb="20px"
      alignItems="center"
      overflow="auto"
    >
      <FiltersAuditoria
        onSearch={handleSearch}
        onClear={handleClear}
        onVisaoChange={handleVisaoChange}
        roleKeycloack={roleKeycloack}
        loading={loading}
      />

      {pesquisou && (
        <Flex
          w="100%"
          flexDir="column"
          backgroundColor="#FFF"
          borderRadius="10px"
          p="18px 24px"
        >
          <Text fontWeight={700} fontSize="16px" color="#252733" mb="14px">
            Resultados
          </Text>

          <TableAuditoria
            columns={visao === "SERVICO" ? columnsServico : columnsCrianca}
            data={result.content}
            loading={loading}
            entidade={
              visao === "SERVICO" ? "auditoria-servico" : "auditoria-crianca"
            }
            pageIndex={pageIndex}
            pageSize={pageSize}
            pageCount={result.totalPages}
            totalItems={result.totalElements}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            sortField={sortField}
            sortType={sortType}
            onSort={handleSort}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default Auditoria;
